
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLowSessionsClients } from '@/hooks/dashboard/useDashboardClients';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Phone, Mail, ArrowRight } from 'lucide-react';
import { getRemainingSessionsCount } from '@/utils/clientFilters';
import { Link } from 'react-router';

const LowSessionsList: React.FC = () => {
  const { t } = useTranslation();
  const { data: lowSessionsClients = [], isLoading } = useLowSessionsClients();

  // Show only first 5 clients for mobile-friendly display
  const displayedClients = lowSessionsClients.slice(0, 5);
  const hasMore = lowSessionsClients.length > 5;

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-3">
              <div className="flex justify-between items-start">
                <div className="space-y-2 flex-1">
                  <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                  <div className="h-2 bg-gray-200 rounded w-1/2"></div>
                </div>
                <div className="h-5 bg-gray-200 rounded w-12"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (lowSessionsClients.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <AlertTriangle className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>{t('dashboard.noLowSessionsClients')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {displayedClients.map((client) => {
        const remainingSessions = getRemainingSessionsCount(client);
        const activeSubscriptions = client.subscriptions.filter(sub => sub.isActive);
        
        return (
          <Link key={client.id} to={`/client/${client.id}`} className="block">
            <Card className="border-l-4 border-l-orange-500 cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="p-3">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-sm">
                      {client.firstName} {client.lastName}
                    </h3>
                    <Badge variant="secondary" className="text-xs">
                      {activeSubscriptions.length} {t('clients.subscriptions')}
                    </Badge>
                  </div>
                  
                  <div className="text-xs text-muted-foreground space-y-3">
                    <div className="space-y-1">
                      {activeSubscriptions.map((sub) => {
                        const subRemaining = sub.sessionsPerMonth - sub.currentCycleSessionsUsed;
                        return (
                          <div key={sub.id} className="flex items-center justify-between text-xs">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: sub.workoutType.color }}></span>
                              <span>{sub.workoutType.name}</span>
                            </div>
                            <span className="font-semibold text-sm text-foreground">{sub.currentCycleSessionsUsed}/{sub.sessionsPerMonth}</span>
                          </div>
                        );
                      })}
                    </div>
                    
                    <div className="flex gap-1 mt-3">
                      {client.phone && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          asChild 
                          className="h-7 px-2"
                        >
                          <a 
                            href={`tel:${client.phone}`} 
                            className="flex items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Phone className="h-3 w-3" />
                          </a>
                        </Button>
                      )}
                      
                      {client.email && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          asChild 
                          className="h-7 px-2"
                        >
                          <a 
                            href={`mailto:${client.email}`} 
                            className="flex items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Mail className="h-3 w-3" />
                          </a>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        );
      })}
      
      {hasMore && (
        <Button variant="outline" size="sm" asChild className="w-full">
          <Link to="/clients?sessions=low" className="flex items-center justify-center gap-2">
            <span>{t('dashboard.seeAllLowSessions')}</span>
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      )}
    </div>
  );
};

export default LowSessionsList;
